'use client';

import { db } from '@/firebase';
import { IonAvatar, IonButton, IonButtons, IonCheckbox, IonContent, IonHeader, IonImg, IonPage, IonSearchbar, IonThumbnail, IonTitle, IonToolbar } from '@ionic/react';
import { Avatar, AvatarImage, AvatarFallback } from '@radix-ui/react-avatar';
import Avvvatars from 'avvvatars-react';
import { profile } from 'console';
import { get, onValue, push, ref, set } from 'firebase/database';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

type NewChatViewProps = {
  userId: string;
  closeNewChat: () => void;
  setActiveChat: React.Dispatch<React.SetStateAction<string | null>>;
};

type User = {
  id: string;
  name: string;
  avatar: string;
};

export default function NewChatView({ userId, closeNewChat, setActiveChat }: NewChatViewProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreatingChat, setIsCreatingChat] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const { t } = useTranslation();

  useEffect(() => {
    const usersRef = ref(db, 'users');
    onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      const userList: User[] = data
        ? Object.entries(data).map(([id, details]: any) => ({
          id,
          name: details.name,
          username: details.username,
          avatar: details.avatar,
        }))
        : [];
      setUsers(userList);
    });
  }, []);

  const filteredUsers = users.length > 0 ? users
    .filter(user =>
      user.id !== userId && user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()),
    )
    .sort((a, b) => a.name.localeCompare(b.name)) : [];

  const toggleUserSelection = (userId: string) => {
    setSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId],
    );
  };

  const startNewChat = async () => {
    if (isCreatingChat || selectedUsers.length === 0) {
      return;
    }
    setIsCreatingChat(true);

    const chatsRef = ref(db, 'chats');
    const participants = [userId, ...selectedUsers];
    const isDirectChat = selectedUsers.length === 1;

    try {
      if (isDirectChat) {
        // Check for existing direct chat
        const snapshot = await get(chatsRef);
        const data = snapshot.val();
        const existingChat = data
          ? Object.entries(data).find(([_, chatDetails]: any) => {
            return (
              chatDetails.type === 'direct'
              && chatDetails.participants
              && chatDetails.participants.includes(userId)
              && chatDetails.participants.includes(selectedUsers[0])
              && chatDetails.participants.length === 2
            );
          })
          : null;

        if (existingChat) {
          const [chatId] = existingChat;
          setActiveChat(chatId);
          closeNewChat();
          return;
        }
      }

      // Create new chat (direct or group)
      const newChatRef = push(chatsRef);
      const newChat = {
        participants,
        type: isDirectChat ? 'direct' : 'group',
        createdAt: new Date().toISOString(),
        createdBy: userId,
      };

      await set(newChatRef, newChat);
      setActiveChat(newChatRef.key || null);
      closeNewChat();
    } catch (error) {
      console.error('Failed to create chat:', error);
    } finally {
      setIsCreatingChat(false);
    }
  };

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={closeNewChat}>
              <ArrowLeft />
            </IonButton>
          </IonButtons>
          <IonTitle>{t('new_chat')}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              onClick={startNewChat}
              disabled={selectedUsers.length === 0 || isCreatingChat}
            >
              {selectedUsers.length > 1 ? t('create_group') : t('start')}
            </IonButton>
          </IonButtons>
        </IonToolbar>
        <IonToolbar className="ion-no-padding">
          <IonSearchbar placeholder={t('search_players')} value={searchTerm} onIonInput={(e) => setSearchTerm(e.detail.value || '')}></IonSearchbar>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="flex flex-col h-full bg-card">
          {/* Scrollable User List */}
          <div className="flex-1 overflow-y-auto">
            {filteredUsers.map(user => (
              <div
                tabIndex={0}
                role="button"
                key={user.id}
                onKeyDown={() => toggleUserSelection(user.id)}
                onClick={() => toggleUserSelection(user.id)}
                className="flex w-full cursor-pointer items-center p-2 hover:bg-neutral-200 dark:hover:bg-neutral-800"
              >
                <div className="flex flex-1 items-center gap-4">
                  <Avatar className="size-10 rounded-full">
                    <AvatarImage className="size-10 rounded-full object-cover" height={64} width={64} src={user.avatar} alt="avatar picture" />
                    <AvatarFallback>
                      <Avvvatars size={40} value={user.name} />
                    </AvatarFallback>
                  </Avatar>
                  <p className="font-semibold">{user.name}</p>
                </div>
                <IonCheckbox
                  color="secondary"
                  checked={selectedUsers.includes(user.id)}
                  onChange={() => toggleUserSelection(user.id)}
                />
              </div>
            ))}
          </div>
        </div>
      </IonContent>
    </>
  );
}
